import { useEffect, useRef } from 'react';
import { Group } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { setEditableFacets, setFacetIntersections } from 'store/slices/RoofEditSlice';
import EditableFacet from '../Facet/EditFacet/EditableFacet';
import { injectTouchingPoints } from '../Facet/utils';
import { isIntersectionAreaWithInThreshold, validatePolygon } from '../Facet/EditFacet/polygonValidator';
import { INVALID_AREA_THRESHOLD } from '../Facet/constant';
import { useRefs } from 'contexts/RefContext';
import { appToast } from 'components/Basic/CustomToast';
import EditablePoints from '../Facet/EditFacet/EditablePoints';
import { useSyncMergedFacetDeletions } from '../Facet/EditFacet/useMergeFacets';
import Label from '../Facet/Label';
import Konva from 'konva';
import InvalidFacetEditToast from './InvalidModal';

type Props = {
	roofSegments: RasterRoofSegment[];
	deletedRoofSegIDs: string[];
}

export default function TempEditFacetGroup({ roofSegments, deletedRoofSegIDs }: Props) {

	const { showFacetLabels, invalidFacetIDs, editableFacets } = useSelector((state: RootState) => state.roofEditData.data);
	const dispatch = useDispatch();
	const editFacetGroupRef = useRef<Konva.Group | null>(null);

	const { konvaRef } = useRefs();

	useSyncMergedFacetDeletions();

	// Kept in a ref (not the effect's dep array) purely so the rebuild below can read the latest
	// editableFacets without re-running every time editableFacets itself changes (it dispatches
	// setEditableFacets, so that would loop).
	const editableFacetsRef = useRef(editableFacets);
	useEffect(() => {
		editableFacetsRef.current = editableFacets;
	}, [editableFacets]);

	useEffect(() => {
		if (roofSegments.length > 0) {
			// Segments already merged/deleted locally (server copy is untouched by a merge, so
			// roofSegments still contains them) must never be regenerated here — otherwise a
			// background refetch resurrects a facet the user already merged away.
			let facets = roofSegments
				.filter(eachFacet => !deletedRoofSegIDs.includes(eachFacet.id))
				.map(eachFacet => ({
					segmentId: eachFacet.id,
					coordinates: eachFacet.simplifiedHullCoords,
					isEdited: false,
					isSelected: false,
				}));

			facets = injectTouchingPoints(facets).map(eachFacet => ({ ...eachFacet, isEdited: false, isSelected: false }));

			// roofSegments can change identity from a background refetch (e.g. dsmShadingListener's
			// post-bake changePanel, which only syncs production numbers) while the user is
			// mid-edit. Reassigning is fine for untouched facets, but a facet already being
			// edited/selected keeps its current local state instead of getting clobbered by the
			// freshly rebuilt one.
			const currentBySegmentId = new Map(editableFacetsRef.current.map(f => [f.segmentId, f]));
			facets = facets.map(f => {
				const existing = currentBySegmentId.get(f.segmentId);
				return (existing?.isEdited || existing?.isSelected) ? existing : f;
			});

			dispatch(setEditableFacets({ editableFacets: facets }));
		}
	}, [dispatch, roofSegments]);

	useEffect(() => {
		if (!editableFacets?.length || !konvaRef?.current) return;

		const invalidIDs: string[] = [];

		for (const facetA of editableFacets) {
			// Skip if not edited or empty geometry
			if (!facetA?.isEdited || !facetA.coordinates.length) continue;

			const pointsA = facetA.coordinates.flat();

			// --- Step 1: Validate polygon itself ---
			const validation = validatePolygon(pointsA);

			if (!validation.valid) {
				if (!invalidIDs.includes(facetA.segmentId)) {
					invalidIDs.push(facetA.segmentId);
				}
				console.log('Invalid polygon:', facetA.segmentId, validation.reason);
				continue;
			}

			// --- Step 2: Check intersection with other facets ---
			let intersectsOther = false;
			for (const facetB of editableFacets) {
				if (facetA.segmentId === facetB.segmentId) continue;
				if (!facetB.coordinates.length) continue;

				const pointsB = facetB.coordinates.flat();
				if (
					isIntersectionAreaWithInThreshold(
						pointsA,
						pointsB,
						INVALID_AREA_THRESHOLD
					)
				) {
					intersectsOther = true;
					break;
				}
			}

			if (intersectsOther) {
				if (!invalidIDs.includes(facetA.segmentId)) {
					invalidIDs.push(facetA.segmentId);
				}
				console.log('Invalid polygon:', facetA.segmentId, 'intersecting with others');
			}
		}

		dispatch(setFacetIntersections(invalidIDs));
	}, [editableFacets, konvaRef, dispatch]);

	useEffect(() => {
		if (invalidFacetIDs.length) {
			appToast.sticky(<InvalidFacetEditToast shouldDisable={!invalidFacetIDs.length} />, {
				toastId: 'invalid-facet-error',
				className: 'invalid-error-toast',
				containerId: 'tool-toast'
			});
		} else if (appToast.isActive('invalid-facet-error')) {
			appToast.dismiss({
				id: 'invalid-facet-error',
				containerId: 'tool-toast'  
			});
		}
	}, [invalidFacetIDs, invalidFacetIDs.length]);

	return (
		<Group name='temp-edit-facet' ref={editFacetGroupRef}>
			<Group>
				{
					roofSegments.map((rs) => {
						if (deletedRoofSegIDs.includes(rs.id)) return null;
						return (
							<Group
								key={`edit-facet-line-${rs.id}`}
								name={`edit-facet-line-${rs.id}`}
								onPointerClick={(event) => {
									const targetFacet = event.currentTarget;
									targetFacet.moveToTop();
								}}
							>
								{<EditableFacet segmentId={rs.id} editFacetGroupRef={editFacetGroupRef} />}
								<Label
									simplifiedHullCoords={rs.simplifiedHullCoords ?? rs.hullCoords}
									label={rs.label}
									visible={showFacetLabels}
									roofSegId={rs.id}
								/>
							</Group>
						);
					})
				}
			</Group>

			<Group>
				{
					editableFacets.map((rs) => {
						if (deletedRoofSegIDs.includes(rs.segmentId)) return null;
						return (
							<Group
								key={`edit-facet-dot-${rs.segmentId}`}
								name={`edit-facet-dot-${rs.segmentId}`}
								onPointerClick={(event) => {
									const targetFacet = event.currentTarget;
									targetFacet.moveToTop();
								}}
							>
								{rs.coordinates.flat().map((_, index) => (
									index % 2 === 0 && (
										<EditablePoints
											key={index}
											segmentId={rs.segmentId}
											points={rs.coordinates.flat()}
											index={index}
											currentSegment={rs}
											editFacetGroupRef={editFacetGroupRef}
										/>
									)
								))}
							</Group>
						);
					})
				}
			</Group>
		</Group>
	);
}